import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import React, { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { AdminUser } from "@/pages/admin/users/columns";
import type { UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface SetRolePayload {
  userId: string;
  role: string;
}

interface SetRoleDialogProps {
  user: AdminUser | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  setRoleMutation: UseMutationResult<unknown, Error, SetRolePayload, unknown>;
}

export function SetRoleDialog({
  user,
  isOpen,
  onOpenChange,
  setRoleMutation,
}: SetRoleDialogProps) {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState<string>("");

  useEffect(() => {
    if (isOpen && user) {
      setSelectedRole(user.roles?.[0] || "");
    } else if (!isOpen) {
      setSelectedRole("");
    }
  }, [isOpen, user]);

  const confirmSetRole = () => {
    if (user && selectedRole) {
      setRoleMutation.mutate({ userId: user.id, role: selectedRole });
    } else {
      toast.warning(t("toasts.selectRoleWarning"));
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("admin.users.page.setRoleDialog.title", {
              userIdentifier: user?.email || user?.id,
            })}
          </DialogTitle>
          <DialogDescription>
            {t("admin.users.page.setRoleDialog.description", {
              currentRoles: user?.roles?.join(", ") || t("common.na"),
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="role-select" className="text-right">
              {t("common.role")}
            </Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger id="role-select" className="col-span-3">
                <SelectValue
                  placeholder={t(
                    "admin.users.page.setRoleDialog.selectPlaceholder",
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">
                  {t("admin.users.page.setRoleDialog.roleUser")}
                </SelectItem>
                <SelectItem value="admin">
                  {t("admin.users.page.setRoleDialog.roleAdmin")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={confirmSetRole}
            disabled={setRoleMutation.isPending || !selectedRole}
          >
            {setRoleMutation.isPending
              ? t("common.saving")
              : t("admin.users.page.setRoleDialog.confirmButton")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
